import { useTranslation } from 'react-i18next';

import { useHelpHotjarUrl } from '@hotjar/translations';
import type { TrackableSite } from '../../constants-types/ExternalDependencyTypes';
import type { AggregatePageInsightsComponentProps } from '../../constants-types/AggregatePageInsights';
import { useGetPageAverageVisitDuration } from '../../hooks/useGetPageAverageVisitDuration';
import { SmartPanelSegment } from './SmartPanelBlocks';
import { SmartPanelSpacedText } from './SmartPanelSpacedText';

const READ_MORE_LINK = '/articles/11276556755735#average-time-on-page';

// Copied from 'packages/legacy/src/webapp/features/Highlights/format-time.ts'
// TODO: Extract into a common re-usable place out of legacy.
function formatTime(time: number): string {
  const seconds = Math.round(time / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = String(seconds - minutes * 60);

  return `${minutes}:${remainingSeconds.padStart(2, '0')}`;
}

interface AverageVisitDurationSegmentProps extends AggregatePageInsightsComponentProps {
  currentSite?: TrackableSite;
}

type AverageVisitDurationProps = Required<AverageVisitDurationSegmentProps>;

function PageAverageVisitDuration({
  selectedDateRange,
  selectedTargetingValue,
  selectedDeviceType,
  searchPattern,
  currentSite,
  isDisabled,
  disabledTooltipText,
}: AverageVisitDurationProps): JSX.Element {
  const { t } = useTranslation('heatmaps');
  const helpHotjarUrl = useHelpHotjarUrl();

  const duration = useGetPageAverageVisitDuration({
    selectedDateRange,
    selectedTargetingValue,
    selectedDeviceType,
    searchPattern,
    siteId: currentSite.id,
    enabled: !isDisabled,
  });

  const durationText = duration !== undefined ? `${formatTime(duration)}` : '-';

  const onReadMoreClick = () => {
    window.open(`${helpHotjarUrl}${READ_MORE_LINK}`, '_blank', 'noopener noreferrer');
  };

  return (
    <SmartPanelSegment data-testid="hj-hm-sp-visit-duration">
      <SmartPanelSpacedText
        leftText={t('smart-panel.average-visit-duration')}
        rightText={durationText}
        leftIcon="time"
        isDisabled={isDisabled}
        disabledTooltipText={disabledTooltipText}
        popoverProps={{
          title: t('smart-panel.average-visit-duration'),
          text: t('smart-panel.average-visit-duration-help'),
          actionText: t('smart-panel.read-more'),
          onActionPress: onReadMoreClick,
        }}
      />
    </SmartPanelSegment>
  );
}

export function AverageVisitDurationSegment({
  selectedDateRange,
  selectedTargetingValue,
  selectedDeviceType,
  searchPattern,
  currentSite,
  isDisabled,
  disabledTooltipText,
}: AverageVisitDurationSegmentProps): JSX.Element | null {
  if (currentSite && currentSite.id) {
    return (
      <PageAverageVisitDuration
        selectedDateRange={selectedDateRange}
        selectedTargetingValue={selectedTargetingValue}
        selectedDeviceType={selectedDeviceType}
        searchPattern={searchPattern}
        currentSite={currentSite}
        isDisabled={isDisabled}
        disabledTooltipText={disabledTooltipText}
      />
    );
  }

  return null;
}
